/**
 * <LiveRating> — l'élève note son live (organism).
 *
 * La note d'aujourd'hui (`SatisfactionScreen`, et la boîte posée en quittant un live de plus de
 * quinze minutes) : cinq étoiles à toucher (`<Satisfaction onRate>`), puis la phrase que le
 * serveur associe à la note (« Super note ! Qu'as-tu particulièrement apprécié ? ») et ses chips
 * de commentaire, qu'on coche et décoche (`<ChoiceChip>`). Les phrases et les chips viennent du
 * serveur (`satisfaction_subtitles`, `satisfaction_student_comments`) : la page ne les écrit pas.
 *
 * Elle ne pose ni titre ni bouton : la feuille qui la porte (`<LiveRatingModal>`) a les siens.
 * Contrôlée : la note et les chips cochées arrivent en props.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Satisfaction } from "../../atoms/Satisfaction"
import { Text } from "../../atoms/typography/Text"

export type LiveRatingProps = {
    /** La note donnée, 1 à 5. `null` : rien encore. */
    rating: number | null
    onRate: (rating: number) => void
    /** La phrase de cette note, que sert le serveur. */
    subtitle?: string
    /** Les chips de cette note, que sert le serveur. */
    comments?: string[]
    /** Les chips cochées. */
    selectedComments?: string[]
    onToggleComment?: (comment: string) => void
    testID?: string
}

const STAR_SIZE = 40
const NO_COMMENTS: string[] = []

export function LiveRating(props: LiveRatingProps) {
    const {
        rating,
        onRate,
        subtitle,
        comments = NO_COMMENTS,
        selectedComments = NO_COMMENTS,
        onToggleComment,
        testID,
    } = props
    const rated = rating != null

    return (
        <Stack gap="4" align="center" testID={testID}>
            <Satisfaction rating={rating ?? 0} size={STAR_SIZE} onRate={onRate} />
            {rated && subtitle != null && subtitle !== "" && (
                <Text size="text-md" color="text-muted" center>
                    {subtitle}
                </Text>
            )}
            {rated && comments.length > 0 && (
                <View style={styles.chips}>
                    <Row gap="2" style={styles.wrap}>
                        {comments.map((comment) => (
                            <ChoiceChip
                                key={comment}
                                label={comment}
                                selected={selectedComments.includes(comment)}
                                onPress={() => onToggleComment?.(comment)}
                            />
                        ))}
                    </Row>
                </View>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    chips: { alignSelf: "stretch" },
    wrap: { flexWrap: "wrap", justifyContent: "center" },
}))
